import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import theme from '../../../theme';

const clearIconSvg = (
  <svg
    width={theme.sizes.icon}
    height={theme.sizes.icon}
    viewBox="0 0 19 19"
    stroke={theme.colors.textHint}
    strokeWidth="2"
    fill="none"
  >
    <line x1="4" y1="4" x2="15" y2="15" />
    <line x1="15" y1="4" x2="4" y2="15" />
  </svg>
);

const dropdownIconSvg = (
  <svg
    width={theme.sizes.icon}
    height={theme.sizes.icon}
    viewBox="0 0 19 19"
    stroke={theme.colors.textHint}
    strokeWidth="2"
    fill="none"
  >
    <polyline points="4,7 9.5,13 15,7" />
  </svg>
);

const indicatorButtonStyle = {
  padding: theme.spacing.iconPadding,
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
};

function ClearIndicator({ innerProps }) {
  return (
    <button
      type="button"
      ref={innerProps.ref}
      onMouseDown={innerProps.onMouseDown}
      onTouchEnd={innerProps.onTouchEnd}
      style={indicatorButtonStyle}
      aria-label="Clear selection"
    >
      {clearIconSvg}
    </button>
  );
}

ClearIndicator.propTypes = {
  innerProps: PropTypes.shape({
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    onMouseDown: PropTypes.func,
    onTouchEnd: PropTypes.func,
  }).isRequired,
};

function DropdownIndicator({ innerProps }) {
  return (
    <button
      type="button"
      ref={innerProps.ref}
      onMouseDown={innerProps.onMouseDown}
      onTouchEnd={innerProps.onTouchEnd}
      style={indicatorButtonStyle}
      aria-label="Toggle menu"
    >
      {dropdownIconSvg}
    </button>
  );
}

DropdownIndicator.propTypes = {
  innerProps: PropTypes.shape({
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    onMouseDown: PropTypes.func,
    onTouchEnd: PropTypes.func,
  }).isRequired,
};

const customComponents = { ClearIndicator, DropdownIndicator };

const customStyles = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    borderRadius: theme.radii.input,
    border: state.isFocused
      ? `1.5px solid ${theme.colors.focusRing}`
      : theme.borders.input,
    boxShadow: state.isFocused
      ? `0 0 0 2px ${theme.colors.focusRing}33`
      : 'none',
    minHeight: theme.sizes.selectMinHeight,
    transition: theme.transitions.default,
    '&:hover': {
      borderColor: theme.colors.focusRing,
    },
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    ...theme.typography.body,
    color: theme.colors.textHint,
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    ...theme.typography.body,
    color: theme.colors.textBody,
  }),
  multiValue: (baseStyles) => ({
    ...baseStyles,
    borderRadius: theme.radii.input,
  }),
  clearIndicator: (baseStyles) => ({
    ...baseStyles,
    padding: theme.spacing.iconPadding,
    cursor: 'pointer',
    '&:hover svg': {
      stroke: theme.colors.focusRing,
    },
  }),
  dropdownIndicator: (baseStyles) => ({
    ...baseStyles,
    padding: theme.spacing.iconPadding,
    cursor: 'pointer',
    '&:hover svg': {
      stroke: theme.colors.focusRing,
    },
  }),
  multiValueRemove: (baseStyles) => ({
    ...baseStyles,
    color: theme.colors.textHint,
    '&:hover': {
      color: theme.colors.danger,
      backgroundColor: theme.colors.dangerBg,
    },
  }),
};

function MultiSelect({
  options,
  onChange,
  value,
  isLoading,
  isDisabled,
  maxMenuHeight,
  getOptionLabel,
  getOptionValue,
  width,
}) {
  const mergedStyles = {
    ...customStyles,
    control: (baseStyles, state) => {
      const themed = customStyles.control(baseStyles, state);
      return {
        ...themed,
        ...(width ? { width } : {}),
      };
    },
  };

  return (
    <Select
      options={options}
      onChange={onChange}
      value={value}
      isLoading={isLoading}
      isDisabled={isDisabled}
      maxMenuHeight={maxMenuHeight}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      components={customComponents}
      styles={mergedStyles}
    />
  );
}

MultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  width: PropTypes.string,
};

MultiSelect.defaultProps = {
  value: undefined,
  isLoading: false,
  isDisabled: false,
  maxMenuHeight: undefined,
  getOptionLabel: undefined,
  getOptionValue: undefined,
  width: null,
};

export default MultiSelect;
